import React, { useState } from 'react';
import {
  Box, Typography, TextField, Collapse, Paper, Chip, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable } from '@dnd-kit/core';

function DraggableFeature({ feature, isRestricted }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${feature.feature_key || feature.key}`,
    data: {
      type: 'feature',
      featureKey: feature.feature_key || feature.key,
      featureType: feature.feature_type || feature.type,
      constraints: feature.constraints,
      displayName: feature.display_name
    }
  });

  return (
    <Tooltip title={feature.description || ''} placement="right" arrow>
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: 'grab',
          opacity: isDragging ? 0.4 : isRestricted ? 0.5 : 1,
          backgroundColor: isRestricted ? 'action.disabledBackground' : 'transparent',
          '&:hover': { backgroundColor: 'action.hover' },
          transition: 'opacity 0.2s, background-color 0.2s'
        }}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography variant="body2" sx={{ flex: 1, textDecoration: isRestricted ? 'line-through' : 'none' }}>
          {feature.display_name}
        </Typography>
        <Chip
          label={feature.feature_type || feature.type}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      </Box>
    </Tooltip>
  );
}

export default function FeaturePalette({ groups, restrictedKeys, isDraft }) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const filteredGroups = groups.map(group => ({
    ...group,
    features: (group.features || []).filter(f =>
      !search || f.display_name.toLowerCase().includes(search.toLowerCase())
    )
  })).filter(g => g.features.length > 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 280,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: 'fit-content',
        maxHeight: '80vh',
        position: 'sticky',
        top: 80
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Feature Palette
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search features..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {filteredGroups.map(group => (
          <Box key={group.key || group.label}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' }
              }}
              onClick={() => setCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
            >
              {collapsed[group.key] ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              <Typography variant="caption" fontWeight="bold" textTransform="uppercase">
                {group.label || group.key}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({group.features.length})
              </Typography>
            </Box>
            <Collapse in={!collapsed[group.key]}>
              <Box sx={{ pb: 0.5 }}>
                {group.features.map(feature => (
                  <DraggableFeature
                    key={feature.feature_key || feature.key}
                    feature={feature}
                    isRestricted={restrictedKeys.has(feature.feature_key || feature.key)}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        ))}

        {filteredGroups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No features match your search
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
